import * as React from 'react'
import * as plume from '@ulule/owl-kit-components/next'

import styles from './Leaderboard.module.css'

/** One rendered row, built from a `Collection` by the section rendering the board. */
export type LeaderboardEntry = {
  rank: number
  name: string
  /** Public project page; the whole row links to it. */
  url: string
  /** Sits under the name; the project's category today. */
  caption: string
  /** Pre-formatted: the amount collected, or what a presale sold. */
  value: string
  /** Overrides the board's column label where a row holds something else — a presale's pre-orders. */
  valueLabel?: string
  /** Board thumbnail; rows without one keep the placeholder glyph. */
  imageUrl?: string
}

export type LeaderboardProps = {
  entries: LeaderboardEntry[]
  /** Describes what the right-hand column holds, for assistive tech. */
  valueLabel: string
  /** Called with the opened row's rank; the section owns what a rank means on its board. */
  onEntryClick: (rank: number) => void
}

/** The ranked board behind the all-time section. */
export function Leaderboard({ entries, valueLabel, onEntryClick }: LeaderboardProps): React.ReactElement {
  return (
    <ol className={styles.lb}>
      {entries.map((entry) => (
        <li key={entry.url} className={styles.lb__row}>
          <a
            className={styles.lb__link}
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onEntryClick(entry.rank)}
          >
            <span className={styles.lb__rank} aria-hidden="true">
              {entry.rank}
            </span>
            <span className={styles.lb__thumb} aria-hidden="true">
              {entry.imageUrl === undefined ? (
                <plume.glyphs.stroke.Media size={26} />
              ) : (
                <img className={styles.lb__img} src={entry.imageUrl} alt="" loading="lazy" decoding="async" />
              )}
            </span>
            <span className={styles.lb__id}>
              <span className={styles.lb__name}>{entry.name}</span>
              <span className={styles.lb__cat}>{entry.caption}</span>
            </span>
            <span className={styles.lb__amt}>
              <span className={styles.lb__valueLabel}>{entry.valueLabel ?? valueLabel} </span>
              {entry.value}
            </span>
          </a>
        </li>
      ))}
    </ol>
  )
}
